import { useCallback, useState } from 'react';

import Button from '@components/button/Button';
import { ReportDateRangePicker } from '@components/index';
import { STORAGE_KEYS } from '@constants/storage';
import { INPUT_FOCUS_CLASSNAME } from '@features/lead-management/constants/common';
import {
  getLeadSourceOptions,
  isAllOptionsSelected
} from '@features/lead-management/utils/common';
import { DateRange as UIDateRange } from '@types';
import { getLocalStorageItem } from '@utils/storage';
import { DropdownOption } from 'types/dropdown';

import AgentSelector from '../AgentSelector';
import AsyncMultiSelectDropdown from '../AsyncMultiSelectDropdown';
import { leadDateRangeOptions } from './helper';
import { LeadReportAnalysisProps } from './types';

import {
  useLazyGetCategoriesQuery,
  useLazyGetStatusQuery,
  useLazyGetTagsQuery
} from '../../queries/LeadManagementQuery';
import { LeadCategory, LeadStatus, LeadTag } from '../../types/common';

const LeadReportAnalysis = ({
  formData,
  onFormChange,
  onDownloadReport,
  isFormValid,
  isGenerating = false,
  handleAllOptionsSelection
}: LeadReportAnalysisProps) => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const [getStatusOptions] = useLazyGetStatusQuery();
  const [getTags] = useLazyGetTagsQuery();
  const [getCategories] = useLazyGetCategoriesQuery();

  const [currentOption, setCurrentOption] = useState<string>('');

  const handleDateRangeSelect = (dateRange: UIDateRange) => {
    onFormChange('startDate', dateRange.startDate);
    const endDateWithTime = new Date(dateRange.endDate);
    endDateWithTime.setHours(23, 59, 59, 999);
    onFormChange('endDate', endDateWithTime);
  };

  const mapStatusOptions = useCallback(
    (data: LeadStatus[]) =>
      data.map((item: LeadStatus) => ({
        id: item.id,
        value: item.value,
        label: item.value
      })),
    []
  );

  const mapTagsOptions = (data: LeadTag[]) =>
    data
      ? data.map((item: LeadTag) => ({
          id: item.id,
          value: item.id,
          label: item.name
        }))
      : [];

  const mapCategoryOptions = useCallback(
    (data: LeadCategory[]) =>
      data.map((item: LeadCategory) => ({
        id: item.id,
        value: item.id,
        label: item.value
      })),
    []
  );

  const handleMultiSelectChange = (
    field: string,
    selectedOptions: DropdownOption[],
    options: DropdownOption[]
  ) => {
    const isAllSelected = isAllOptionsSelected(selectedOptions, options);
    handleAllOptionsSelection(field, isAllSelected);
    onFormChange(field, selectedOptions);
  };

  return (
    <div className="rounded-xl bg-white p-6">
      <div className="mb-6 flex flex-wrap gap-6">
        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)]">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Lead Creation Date <span className="text-red-500">*</span>
          </label>
          <ReportDateRangePicker
            currentFilter={currentOption}
            filterOptions={leadDateRangeOptions}
            onSelectFilter={(dateRange, option) => {
              handleDateRangeSelect(dateRange);
              setCurrentOption(option);
            }}
            customClass="w-full"
            customDropdownClass=" min-h-[610px]"
            buttonClassName={`!bg-theme ${INPUT_FOCUS_CLASSNAME}`}
          />
        </div>

        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)]">
          <AsyncMultiSelectDropdown
            label="Lead Category"
            placeholder="Select Lead Category"
            fetchOptions={() => getCategories({ organizationId }).unwrap()}
            map={mapCategoryOptions}
            selectedOptions={formData.leadCategory}
            setSelectedOptions={(value, options) =>
              handleMultiSelectChange('leadCategory', value, options)
            }
            chipToolTipId="lead-category-tooltip"
            errorAlertMessage="Error fetching categories"
          />
        </div>

        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)]">
          <AgentSelector
            selectedAgents={formData.assignedAgent}
            setSelectedAgents={(agents, options) =>
              handleMultiSelectChange('assignedAgent', agents, options)
            }
            organizationId={organizationId}
            showUnassignedAgent
          />
        </div>

        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)]">
          <AsyncMultiSelectDropdown
            label="Lead Status"
            placeholder="Select Lead Status"
            fetchOptions={() => getStatusOptions({ organizationId }).unwrap()}
            map={mapStatusOptions}
            selectedOptions={formData.leadStatus}
            setSelectedOptions={(value, options) =>
              handleMultiSelectChange('leadStatus', value, options)
            }
            chipToolTipId="lead-status-tooltip"
            errorAlertMessage="Error fetching status options"
            mustSelectAtLeastOne
          />
        </div>

        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)]">
          <AsyncMultiSelectDropdown
            label="Source"
            placeholder="Select Source"
            listItems={getLeadSourceOptions()}
            selectedOptions={formData.source}
            setSelectedOptions={(value, options) =>
              handleMultiSelectChange('source', value, options)
            }
            chipToolTipId="lead-source-tooltip"
            mustSelectAtLeastOne
          />
        </div>

        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)] ">
          <AsyncMultiSelectDropdown
            label="Tags"
            placeholder="Select Tags"
            fetchOptions={() => getTags({ organizationId }).unwrap()}
            map={mapTagsOptions}
            selectedOptions={formData.tags}
            setSelectedOptions={(value, options) =>
              handleMultiSelectChange('tags', value, options)
            }
            chipToolTipId="lead-tags-tooltip"
            errorAlertMessage="Error fetching tags"
          />
        </div>
      </div>

      {/* <div className="flex flex-wrap gap-6">
        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)]">
          <label className="mb-3 block text-sm font-medium text-gray-700">
            Add to groups
          </label>
          <div className="flex space-x-6">
            <Checkbox
              label="Yes"
              isChecked={formData.addToGroups?.includes('yes') || false}
              handleChange={(e) =>
                handleCheckboxChange('addToGroups', 'yes', e.target.checked)
              }
              htmlKey="add-to-groups-yes"
            />
            <Checkbox
              label="No"
              isChecked={formData.addToGroups?.includes('no') || false}
              handleChange={(e) =>
                handleCheckboxChange('addToGroups', 'no', e.target.checked)
              }
              htmlKey="add-to-groups-no"
            />
          </div>
        </div>

        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)]">
          <label className="mb-3 block text-sm font-medium text-gray-700">
            Payment Link Sent
          </label>
          <div className="flex space-x-6">
            <Checkbox
              label="Yes"
              isChecked={formData.paymentLinkSent?.includes('yes') || false}
              handleChange={(e) =>
                handleCheckboxChange('paymentLinkSent', 'yes', e.target.checked)
              }
              htmlKey="payment-link-yes"
            />
            <Checkbox
              label="No"
              isChecked={formData.paymentLinkSent?.includes('no') || false}
              handleChange={(e) =>
                handleCheckboxChange('paymentLinkSent', 'no', e.target.checked)
              }
              htmlKey="payment-link-no"
            />
          </div>
        </div>
      </div> */}

      <div className="pt-6">
        <Button
          variant="primary"
          size="large"
          label="Download Report"
          handleClick={onDownloadReport}
          disabled={!isFormValid}
          loading={isGenerating}
          className="w-full px-8 py-3 md:w-auto"
        />
      </div>
    </div>
  );
};

export default LeadReportAnalysis;
